import { Dispatch, SetStateAction } from 'react';

import {
    ASDRatchets,
    AmuContractTerms,
    ContractDeal,
    CostPerPlay,
    EntityActiveIntervals,
    MonthlyActiveUser,
    PerformanceDiscounts,
    PlayCountAggregationTypeMappingList,
    PlayType,
    Ratchet,
    RatchetModel,
    RateMultiplierType,
    StreamingTaxDeductionCaps,
    TerritoryCodeList,
    TerritoryOverrides,
    VendorShareCalculationTypeMappingList,
    VpirBundleRevenueDeduction
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { RateCardStatus, ServiceAbbr } from 'src/constants';
import { RateCardCoreRateGridColumnName } from 'src/constants/enums';

import { IMetadata } from './IMetadata';

// Rate card v2 interface
export interface IRateCardsRowData extends ContractDeal {
    status?: RateCardStatus;
}

export interface IAddRateCardModalValues {
    vendor: string;
    serviceId: string;
    month: string;
    description?: string;
    templateRateCardId?: string;
}

export interface IRateCardDetailStepProps {
    rateCardValues: IRateCardValues;
    setRateCardValues: Dispatch<SetStateAction<IRateCardValues>>;
    isRateCardInputValid?: Record<string, boolean>;
    setIsRateCardInputValid?: Dispatch<SetStateAction<Record<string, boolean>>>;
    stepTitle?: string;
    onCancel?: () => void;
    onPrevious?: () => void;
    onNext?: () => void;
    onSubmit?: () => void;
    onSaveDraft?: () => void;
    isEditMode: boolean;
    multiplierTypeList?: IMultiplierType[];
    corePlanList?: IFirstPartyCorePlan[];
    planNameToMetadataObj?: IPlanNameToMetadataObj;
    setIsNextDisabled?: Dispatch<SetStateAction<boolean>>;
    setIsPrevDisabled?: Dispatch<SetStateAction<boolean>>;
    augmentedMetadata?: IMetadata;
    dataLoaded?: boolean;
}

export interface IContractTermsComponentProps {
    contractTerms: AmuContractTerms;
    coreRate?: IRateCardCoreRateGridRowData[];
    isEditMode: boolean;
    isContractTermInputValid?: Record<string, boolean>;
    setIsContractTermInputValid?: Dispatch<SetStateAction<Record<string, boolean>>>;
    handleContractTermValueChange: (
        sectionKey: string,
        key: string,
        value:
            | string
            | boolean
            | TerritoryOverrides
            | PerformanceDiscounts
            | ASDRatchets
            | VpirBundleRevenueDeduction
            | VendorShareCalculationTypeMappingList
            | PlayCountAggregationTypeMappingList
            | StreamingTaxDeductionCaps
            | null
            | undefined
    ) => void;
    contractTermErrorMessages?: Record<string, Record<string, string>>;
    handleContractTermErrorMessageChange?: (sectionKey: string, key: string, value: string) => void;
    optionalSection?: string[];
    setOptionalSection?: React.Dispatch<React.SetStateAction<string[]>>;
}

export interface IRateCardDetailParams {
    rateCardId: string;
    editType?: 'add' | 'edit';
}

export interface IRateCardHeaderValues {
    contractName: string;
    description: string;
    isDeleted: boolean;
    isDraft: boolean;
    createdBy: string;
    creationDate: string | Date;
    changedBy: string | undefined;
    changedDate: string | Date | undefined;
    entityActiveIntervals: EntityActiveIntervals;
}

export interface IRateCardValues extends IRateCardHeaderValues {
    serviceId: number;
    coreRates: IRateCardCoreRateGridRowData[];
    contractTerms: AmuContractTerms;
    firstPartyRates: IFirstPartyRateGridRowData[];
    thirdPartyData: IThirdPartyData;
    fullRates: IFullRateGridRowData[];
    planConfiguration: ICoreRatesPlanConfiguration;
}

export interface IPrimeAMFRateCardValues extends IRateCardHeaderValues {
    serviceId: number;
    primeContractTerms?: IPrimeContractTerms;
    amfContractTerms?: IAmfContractTerms;
    rateCardRowData: IPrimeAMFRateCardGridRowData;
}

export interface IPrimeContractTerms {
    costPerPlay?: ICostPerPlay;
    monthlyActiveUser?: MonthlyActiveUser;
    prsDeductionCap?: IPrsDeductionCap;
    adRevShare?: IAdRevShare;
}

export interface IPrsDeductionCap {
    type: string;
    value: string;
}

export interface ICostPerPlay extends CostPerPlay {
    householdSharingTerritoryIds?: Set<number>;
}

export interface IMonthlyActiveUser {
    customerTypes: string[];
}

export interface IAmfContractTerms {
    adRevShare: IAdRevShare;
}

export interface IAdRevShare {
    revSharePercent: string;
    playTypes: PlayType[];
    conversions?: RatchetModel[];
    territories?: TerritoryCodeList;
}

export interface IRatchetModel extends RatchetModel {
    ratchets: IRatchet[];
}

export interface IRatchet extends Ratchet {
    name: string;
}

export interface IProgramType {
    programTypeId: number;
    programTypeName: string;
}

export interface IPrimeAMFRateCardGridRowData {
    cpp: IRateCardGridRowData[];
    mau: IRateCardGridRowData[];
}

export interface IRateCardGridRowData {
    [key: string]: string | number;
    territoryId: number;
    rateCardCurrency: string;
    settlementCurrency: string;
    cae: string;
    playType: string;
    programType: string;
    rate: string;
}

export interface IUmgPerfDiscountRowData {
    threshold: string;
    discount: string;
    discountPercentsWithoutPrimeOrAMF: string;
}

// Keep this interface in case the usage of the ContractTerms coral model is unsuitable for add/edit
export interface IContractTerms {
    revenue: IRevenue;
    rateMultipliers: IRateMultipliers;
    asdRatchets: IAsdRatchets;
    thirdPartyDeductionCaps: IThirdPartyDeductionCaps;
    subscriberVolumeDiscount: ISubscriberVolumeDiscount;
    additionalSettings: IAdditionalSettings;
}

export interface IRevenue {
    revenueSharePercentage: string;
    includeBadDebt: boolean;
    deductChargebacks: boolean;
}

export interface IRateMultipliers {
    daily: string;
    weekly: string;
    annual: string;
    priceDrop3m: string;
    priceDrop4m: string;
    winback: string;
    extendedTrial2m: string;
    extendedTrial3m: string;
    extendedTrial4m: string;
    extendedTrial5m: string;
    extendedTrial6m: string;
}

export interface IAsdRatchets {
    rollingMonthlyAverage: string;
    inputDefinitions: string;
    tier2Threshold: string;
    tier3Threshold: string;
    thresholdOperator: string;
}

export interface IThirdPartyDeductionCaps {
    thirdPartyBilling: string;
    territoryOverrides: ITerritoryOverride[];
    appStore: string;
    carrierBilling: string;
}

export interface ITerritoryOverride {
    territory: string;
    deductionCap: string;
}

export interface ISubscriberVolumeDiscount {
    threshold: string;
    ratesReducedByFraction: string;
}

export interface IAdditionalSettings {
    applyAntiDilution: boolean;
    studentTrueUpThreshold: boolean;
    studentTrueUpThresholdValue: string;
}

export const CORE_RATE_GRID_PLAN_COLUMNS = [
    RateCardCoreRateGridColumnName.IndMonthlyPSM,
    RateCardCoreRateGridColumnName.HDIndMonthlyPSM,
    RateCardCoreRateGridColumnName.ASDMonthlyTier1PSM,
    RateCardCoreRateGridColumnName.FamMonthlyPSM,
    RateCardCoreRateGridColumnName.HDFamMonthlyPSM,
    RateCardCoreRateGridColumnName.StudentMonthlyPSM,
    RateCardCoreRateGridColumnName.NinetyDayFTCPP,
    RateCardCoreRateGridColumnName.HDNinetyDayFTCPP,
    RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM,
    RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM,
    RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP
];

export type CoreRateGridPlanColumns = (typeof CORE_RATE_GRID_PLAN_COLUMNS)[number];

export interface IRateCardMetadata {
    royaltyModelTypeCode: string;
    corePlanModelCode: string;
    warnings?: string[];
}

export type ICoreRatesPlanConfiguration = {
    [key in CoreRateGridPlanColumns]?: IRateCardMetadata;
};

export interface IRateCardCoreRateGridRowData {
    [key: string]: string | undefined;
    [RateCardCoreRateGridColumnName.Territory]: string | undefined;
    [RateCardCoreRateGridColumnName.RatecardCurrency]: string | undefined;
    [RateCardCoreRateGridColumnName.SettlementCurrency]?: string;
    [RateCardCoreRateGridColumnName.CAE]?: string | undefined;
    [RateCardCoreRateGridColumnName.IndMonthlyPSM]: string;
    [RateCardCoreRateGridColumnName.HDIndMonthlyPSM]: string;
    [RateCardCoreRateGridColumnName.ASDMonthlyTier1PSM]: string;
    [RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM]?: string;
    [RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM]?: string;
    [RateCardCoreRateGridColumnName.FamMonthlyPSM]: string;
    [RateCardCoreRateGridColumnName.HDFamMonthlyPSM]: string;
    [RateCardCoreRateGridColumnName.StudentMonthlyPSM]: string;
    [RateCardCoreRateGridColumnName.NinetyDayFTCPP]?: string;
    [RateCardCoreRateGridColumnName.HDNinetyDayFTCPP]?: string;
    [RateCardCoreRateGridColumnName.BlendedPSMFloor]?: string;
}

export interface IPlanNameToMetadataObj {
    [planName: string]: Record<string, string | boolean | number>;
}

export interface IRatchetModelIdtoRatchetsObj {
    [id: number]: Ratchet[];
}

export interface IMultiplierType extends RateMultiplierType {
    contractTermMultiplierKey: string;
    displayName: string;
}

export interface IFirstPartyCorePlan {
    corePlanId: string;
    corePlanName: string;
    corePlanDisplayName: string;
}

export interface IFirstPartyRateGridRowData {
    [key: string]: string | number | undefined;
    displayName: string;
    paperPlanName: string;
    rateGroup: string;
    planGroup: string;
    planType: string;
    customerPays: string;
    isPromotion: string;
    renewalCadence: string;
    promoDurationInDays: string;
    corePlan: string;
    corePlanId?: string;
    rateType: string;
    multiplierName: string;
    multiplierValue: string;
    multiplierTypeId?: number;
    approvedBy: string;
    approvedDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
}

export interface IThirdPartyRateGridRowData extends IFirstPartyRateGridRowData {
    territory: string;
    endUserPrice: string;
    wholesalePrice: string;
    thirdPartyCurrency: string;
    corePlanPSM: string;
    rateCardCurrency: string;
    psmDiscCap: string;
    revDiscCap: string;
    rateCalcType: string;
    finalRate: string;
    revCalcType: string;
    serviceRev: string;
}

export interface IThirdPartyData {
    autoCalcRateMultiplierTypeId: number;
    thirdPartyRates: IThirdPartyRateGridRowData[];
}

export interface IFullRateGridRowData {
    territory: string;
    region: string;
    rateCardCurrency: string;
    settlementCurrency: string;
    cae: string;
    displayName: string;
    paperPlanName: string;
    rateGroup: string;
    rateType: string;
    rate: string;
    tier2Rate: string;
    tier3Rate: string;
    serviceRev: string;
}

// Rate card v1 interface
export interface IRevenueInfo {
    rev_share: string | number;
    bad_debt_included: boolean;
    deduct_chargebacks_enabled: boolean;
}

export interface IRevenueDeductionCapsInfo {
    integrated_carrier_value: string | number;
    app_store_value: string | number;
    service_bundle_value: string | number;
    app_store_deduction_cap_based_on: string;
}

export interface IPSMMultiplierInfo {
    id: string;
    name: string;
    multiplier: string | number;
    base_plan: string;
    hidden: boolean;
}

export interface IASDRatchetsTierThreshold {
    id: string;
    threshold: number | string;
}

export interface IASDRatchetsInfo {
    enabled: boolean;
    compound_psm: boolean;
    threshold_operator: string;
    rolling_monthly_average: number;
    threshold_tiers: IASDRatchetsTierThreshold[];
    input_definitions: string;
}

export interface IAdvancedInfo {
    floor_blended_psm: boolean;
    pro_rata_excludes_non_music: boolean;
    student_plan_true_up: boolean;
    student_threshold: string | number;
}

export interface IPSMDiscountCapsInfo {
    integrated_carrier: string | number;
    product_bundle_1_3: string | number;
    product_bundle_4: string | number;
    service_bundle_1_3: string | number;
    service_bundle_4: string | number;
}

export interface ISubscriberVolumeDiscountInfo {
    enabled: boolean;
    threshold: string | number;
    discount: string | number;
}

export interface ISpatialPlayDiscountInfo {
    enabled: boolean;
    threshold: string | number;
    threshold_type: string;
    plan: string;
    territory: string[];
    no_earlier_than: string;
    psm_increase_individual: string | number;
    psm_increase_family: string | number;
    psm_increase_student: string | number;
}

export interface IRateCard {
    id: string;
    vendor: string;
    sequence: string;
    description: string;
    status: RateCardStatus;
    service: ServiceAbbr;
    name: string;
    cpp_plans: {
        [key: string]: boolean;
    };
    revenue: IRevenueInfo;
    revenue_deduction_caps: IRevenueDeductionCapsInfo;
    psm_multipliers: IPSMMultiplierInfo[];
    asd_ratchets: IASDRatchetsInfo;
    psm_discount_caps: IPSMDiscountCapsInfo;
    spatial_play_discount: ISpatialPlayDiscountInfo;
    subscriber_volume_discount: ISubscriberVolumeDiscountInfo;
    umg_2021_4_discounts: boolean;
    advanced: IAdvancedInfo;
    grid_data: IRateCardRowData[];
}

export interface IRateCardRowData {
    country_code: string;
    country_name: string;
    region: string;
    launched: boolean;
    rc_currency: string;
    payment_currency: string;
    entity: string;
    ind_monthly: string | number;
    fam_monthly: string | number;
    stdnt_monthly: string | number;
    asd_monthly?: string | number;
    asd_2m?: string | number;
    asd_3m?: string | number;
    ninety_day_ft: string | number;
    ninety_day_fam_ft: string | number;
    ind_annual: string | number;
    fam_annual: string | number;
    ind_daily: string | number;
    ind_weekly: string | number;
    three_for_99: string | number;
    four_for_99: string | number;
    three_for_100_jp: string | number;
    ext_trial_2m: string | number;
    ext_trial_4m: string | number;
    ext_trial_5m: string | number;
    ext_trial_6m: string | number;
    winback: string | number;
    [key: string]: string | number | boolean | undefined;
}
